import type { ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { AMBIANCES } from '@/components/oasis/ambiances'
import { Legende, Sticker, Titre } from '@/components/oasis'
import { cn, Icon } from '@/components/ui'

/**
 * La porte d'entrée — connexion, mot de passe oublié, réinitialisation, mot de
 * passe provisoire — en DA 2027, d'après `oasis-2027-maquettes/connexion.html`,
 * validée par Baptiste le 2026-10-29.
 *
 * Elle remplace la carte de verre sombre posée sur la photo plein écran. Ce
 * n'est pas un écran de l'Oasis : l'équipe passe par la même porte pour aller
 * au pilotage. C'est pourquoi elle pose elle-même sa racine `data-da="2027"`
 * au lieu d'emprunter `CoqueOasis` — pas de rail, pas de compte.
 *
 * **Trois zones, deux ordres.** Sur grand écran, l'aplat rose et sa photo à
 * gauche, le logo puis le formulaire à droite. Sous 1100px, une colonne : le
 * logo, la photo, le formulaire (demande de Baptiste — la photo avant « Se
 * connecter »). Des aires de grille plutôt qu'un logo écrit deux fois.
 *
 * ⚠️ **Le logo couleur ne se pose que sur le crème**, jamais sur l'aplat rose :
 * c'est la remarque de Baptiste sur la première version de la maquette.
 *
 * ⚠️ Sticker « L'Oasis. » lavande sur l'aplat rose, à cheval sur le bord de la
 * photo : un sticker texte peut chevaucher une photo, une illustration non. Le
 * rose sur rose disparaîtrait.
 */
export function Porte({ children }: { children: ReactNode }) {
  return (
    <div data-da="2027" className="min-h-dvh bg-cream text-ink">
      <div
        className={cn(
          'grid min-h-dvh gap-9 px-4 pt-4 pb-16 [grid-template-areas:"marque"_"decor"_"form"]',
          'oasis:px-[clamp(20px,3vw,40px)] oasis:pt-5',
          'min-[1100px]:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] min-[1100px]:grid-rows-[auto_1fr] min-[1100px]:gap-x-[clamp(32px,4vw,96px)] min-[1100px]:gap-y-0',
          'min-[1100px]:py-7 min-[1100px]:pr-[clamp(24px,3vw,64px)] min-[1100px]:pl-7',
          'min-[1100px]:[grid-template-areas:"decor_marque"_"decor_form"]',
        )}
      >
        <header className="flex flex-wrap items-center justify-between gap-4 [grid-area:marque] min-[1100px]:pt-3">
          <Image
            src="/brand/logo/umiros-principal.png"
            alt="Umiros"
            width={3644}
            height={1246}
            priority
            className="h-[26px] w-auto oasis:h-[34px]"
          />
          <Legende>Formation IA · Métiers créatifs</Legende>
        </header>

        {/* `alt=""` et `aria-hidden` : un décor de marque. La décrire ferait lire
            un paysage avant le titre de l'écran. */}
        <aside
          aria-hidden="true"
          className={cn(
            'relative flex flex-col rounded-[24px] bg-rose p-[22px] [grid-area:decor]',
            'oasis:p-[clamp(28px,2.6vw,56px)]',
            'min-[1100px]:sticky min-[1100px]:top-7 min-[1100px]:h-[calc(100dvh-56px)] min-[1100px]:self-start',
          )}
        >
          <Sticker
            couleur="lavande"
            className="absolute top-0.5 left-[38px] z-10 text-[18px] oasis:top-[calc(clamp(28px,2.6vw,56px)-22px)] oasis:left-[calc(clamp(28px,2.6vw,56px)+24px)] oasis:px-[22px] oasis:py-2.5 oasis:text-[clamp(20px,1.6vw,30px)]"
          >
            L’Oasis.
          </Sticker>
          <div className="relative h-[200px] overflow-hidden rounded-[16px] oasis:h-[clamp(220px,34vw,340px)] oasis:rounded-[24px] min-[1100px]:h-auto min-[1100px]:min-h-[320px] min-[1100px]:flex-1">
            <Image
              src={AMBIANCES.accueil.fichier}
              alt=""
              fill
              priority
              placeholder="blur"
              blurDataURL={AMBIANCES.accueil.flou}
              // ⚠️ Pas « 52vw » : sur grand écran le cadre est en portrait et la
              // photo (2560×1058) le couvre par la hauteur — elle s'affiche
              // ~2,4 fois plus large que haute, soit environ deux fois la hauteur
              // de l'écran. Déclarer la largeur de la colonne servait une image
              // trop petite, floue une fois agrandie.
              sizes="(min-width: 1100px) 220vh, 100vw"
              className="object-cover"
              style={{ objectPosition: 'center 55%' }}
            />
          </div>
        </aside>

        <main className="flex min-w-0 flex-col justify-center [grid-area:form] min-[1100px]:py-[clamp(8px,2vw,40px)]">
          {children}
        </main>
      </div>
    </div>
  )
}

/**
 * Le haut de chaque écran de la porte : légende, titre, chapô.
 *
 * Un seul mot en Playfair, point final compris (`accent`). `retour` pose le
 * lien de retour au-dessus du titre, comme le héros de fiche de l'Oasis.
 */
export function EnTetePorte({
  surtitre,
  titre,
  accent,
  retour,
  children,
}: {
  surtitre?: string
  titre: string
  accent: string
  retour?: { href: string; libelle: string }
  children?: ReactNode
}) {
  return (
    <div className="mb-[clamp(10px,1.6vw,30px)] flex flex-col">
      {retour && (
        <Link
          href={retour.href}
          className="mb-[22px] inline-flex min-h-[44px] items-center gap-2 self-start text-[14px] font-bold text-ink"
        >
          <Icon as={ArrowLeft} size={16} />
          {retour.libelle}
        </Link>
      )}
      {surtitre && <Legende className="mb-3.5">{surtitre}</Legende>}
      <Titre
        as="h1"
        accent={accent}
        className="text-[54px] min-[761px]:text-[clamp(56px,5.2vw,124px)]"
      >
        {titre}
      </Titre>
      {children && (
        <p className="mt-[18px] max-w-[30em] text-[16px] text-ink-soft min-[761px]:text-[clamp(16px,1.1vw,20px)]">
          {children}
        </p>
      )}
    </div>
  )
}

/** Lien de la porte : souligné lavande, prune au survol. */
export function LienPorte({
  href,
  className,
  children,
}: {
  href: string
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      className={cn(
        'inline-flex min-h-[44px] items-center gap-1.5 border-b-2 border-lavender text-[14px] font-bold text-ink hover:border-ink',
        className,
      )}
    >
      {children}
    </Link>
  )
}
